/* Deal Links — console des liens de partage sécurisés à durée limitée.
   Doctrine : un Deal Link est un objet traçable (deal, contenu, destinataire,
   historique d'accès) — pas un simple lien de fichier. */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notify } from "@/lib/notify";
import {
  Link2, Plus, Loader2, Clock, Copy, ShieldCheck, Ban, RotateCcw, Trash2,
  Upload, FileText, Eye, X, ChevronDown,
} from "lucide-react";

const STATUS = {
  active: { label: "Actif", cls: "text-[#1D9E75] border-[#1D9E75]/40 bg-[#1D9E75]/10" },
  expired: { label: "Expiré", cls: "text-[#EF9F27] border-[#EF9F27]/40 bg-[#EF9F27]/10" },
  closed: { label: "Fermé", cls: "text-zinc-400 border-white/15 bg-white/5" },
  destroyed: { label: "Détruit", cls: "text-[#E24B4A] border-[#E24B4A]/40 bg-[#E24B4A]/10" },
};

const remaining = (iso) => {
  const d = new Date(iso) - Date.now();
  if (d <= 0) return "—";
  const h = Math.floor(d / 36e5);
  if (h >= 48) return `${Math.floor(h / 24)} j ${h % 24} h`;
  return `${h} h ${Math.floor((d % 36e5) / 6e4)} min`;
};

function LinkRow({ lk, onAction, onLog }) {
  const st = STATUS[lk.effective_status] || STATUS.closed;
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-t border-white/5 text-[12px]" data-testid={`dl-link-${lk.id}`}>
      <span className={`px-1.5 py-0.5 rounded-full border text-[10px] shrink-0 ${st.cls}`}>{st.label}</span>
      <span className="text-zinc-200 truncate">{lk.recipient?.name}</span>
      <span className="text-zinc-600 truncate hidden md:inline">{lk.recipient?.address}</span>
      {lk.options?.nda_required && <ShieldCheck className="w-3 h-3 text-[#43C6D8] shrink-0" title="Confidentialité requise" />}
      <span className="ml-auto text-zinc-500 flex items-center gap-1 shrink-0"><Clock className="w-3 h-3" />{lk.effective_status === "active" ? remaining(lk.expires_at) : "—"}</span>
      <span className="text-zinc-500 shrink-0">{lk.opens_count || 0} ouv.</span>
      <button onClick={() => onLog(lk)} className="text-zinc-500 hover:text-white" title="Journal" data-testid={`dl-log-${lk.id}`}><Eye className="w-3.5 h-3.5" /></button>
      {lk.effective_status !== "destroyed" && (
        <>
          <button onClick={() => onAction(lk, "extend_24h")} className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-400 hover:text-white" data-testid={`dl-ext24-${lk.id}`}>+24h</button>
          <button onClick={() => onAction(lk, "extend_72h")} className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-400 hover:text-white">+72h</button>
          {lk.effective_status === "active"
            ? <button onClick={() => onAction(lk, "close")} className="text-zinc-500 hover:text-[#EF9F27]" title="Fermer maintenant" data-testid={`dl-close-${lk.id}`}><Ban className="w-3.5 h-3.5" /></button>
            : <button onClick={() => onAction(lk, "reopen")} className="text-zinc-500 hover:text-[#1D9E75]" title="Rouvrir" data-testid={`dl-reopen-${lk.id}`}><RotateCcw className="w-3.5 h-3.5" /></button>}
          <button onClick={() => onAction(lk, "destroy")} className="text-zinc-600 hover:text-[#E24B4A]" title="Détruire définitivement" data-testid={`dl-destroy-${lk.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </>
      )}
    </div>
  );
}

export function NewLinkForm({ roomId, onCreated }) {
  const [f, setF] = useState({ recipient_name: "", recipient_channel: "whatsapp", recipient_address: "", duration: "72h", expires_at: "", nda_required: false, watermark: true });
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  const create = async () => {
    if (!f.recipient_name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/workspace/deal-links/rooms/${roomId}/links`, f);
      setUrl(window.location.origin + data.url_path);
      onCreated?.();
    } catch (e) { notify.error(e?.response?.data?.detail || "Création impossible."); }
    setBusy(false);
  };
  if (url) {
    return (
      <div className="p-3 bg-[#1D9E75]/10 border border-[#1D9E75]/30 rounded-md space-y-2" data-testid="dl-url-box">
        <div className="text-[11px] text-[#1D9E75] font-medium">Lien créé — copiez-le maintenant, il ne sera plus affiché :</div>
        <div className="flex gap-2">
          <input readOnly value={url} className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[11px] text-zinc-300" data-testid="dl-url-input" />
          <button onClick={() => { navigator.clipboard.writeText(url); notify.success("Lien copié."); }} className="px-2 rounded border border-white/15 text-zinc-300 hover:text-white" data-testid="dl-copy-btn"><Copy className="w-3.5 h-3.5" /></button>
        </div>
        <button onClick={() => { setUrl(null); setF((p) => ({ ...p, recipient_name: "", recipient_address: "" })); }} className="text-[11px] text-zinc-400 hover:text-white">+ Autre destinataire</button>
      </div>
    );
  }
  return (
    <div className="p-3 bg-black/20 border border-white/8 rounded-md grid grid-cols-2 md:grid-cols-3 gap-2 text-[12px]" data-testid="dl-new-link-form">
      <input placeholder="Nom du destinataire *" value={f.recipient_name} onChange={(e) => setF({ ...f, recipient_name: e.target.value })} className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-white col-span-2 md:col-span-1" data-testid="dl-recipient-name" />
      <select value={f.recipient_channel} onChange={(e) => setF({ ...f, recipient_channel: e.target.value })} className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-zinc-300">
        <option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="autre">Autre</option>
      </select>
      <input placeholder="Téléphone / email" value={f.recipient_address} onChange={(e) => setF({ ...f, recipient_address: e.target.value })} className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-white" />
      <select value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-zinc-300" data-testid="dl-duration">
        <option value="24h">24 heures</option><option value="72h">72 heures</option><option value="7d">7 jours</option><option value="custom">Date personnalisée</option>
      </select>
      {f.duration === "custom" && (
        <input type="datetime-local" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-zinc-300" data-testid="dl-custom-date" />
      )}
      <label className="flex items-center gap-1.5 text-zinc-400"><input type="checkbox" checked={f.nda_required} onChange={(e) => setF({ ...f, nda_required: e.target.checked })} data-testid="dl-nda-check" /> Confidentialité</label>
      <label className="flex items-center gap-1.5 text-zinc-400"><input type="checkbox" checked={f.watermark} onChange={(e) => setF({ ...f, watermark: e.target.checked })} /> Filigrane</label>
      <button onClick={create} disabled={busy || !f.recipient_name.trim()} className="bg-[#378ADD] hover:bg-[#2f7ac4] disabled:opacity-40 text-white rounded px-3 py-1.5 font-medium flex items-center justify-center gap-1.5" data-testid="dl-create-link-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Générer le lien
      </button>
    </div>
  );
}

export default function DealLinks() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [codeName, setCodeName] = useState("");
  const [busy, setBusy] = useState(false);
  const [openRoom, setOpenRoom] = useState(null);
  const [log, setLog] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/workspace/deal-links/rooms");
      setRooms(data.items || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !codeName.trim()) { notify.error("Nom de code et fichier requis."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("code_name", codeName.trim());
      fd.append("file", file);
      await api.post("/workspace/deal-links/rooms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      notify.success("Dossier créé.");
      setShowUpload(false); setCodeName(""); load();
    } catch (e) { notify.error(e?.response?.data?.detail || "Upload impossible."); }
    setBusy(false);
  };

  const action = async (lk, act) => {
    try {
      if (act === "destroy") {
        if (!window.confirm("Détruire définitivement ce lien ? Cette action est irréversible.")) return;
        await api.delete(`/workspace/deal-links/links/${lk.id}`);
      } else {
        await api.patch(`/workspace/deal-links/links/${lk.id}`, { action: act });
      }
      load();
    } catch (e) { notify.error(e?.response?.data?.detail || "Action impossible."); }
  };

  const closeAll = async (rid) => {
    await api.post(`/workspace/deal-links/rooms/${rid}/close-all`);
    notify.success("Tous les liens du dossier sont fermés.");
    load();
  };

  const destroyRoom = async (rid) => {
    if (!window.confirm("Détruire le dossier et son contenu définitivement ? Les liens seront morts et le contenu effacé.")) return;
    await api.delete(`/workspace/deal-links/rooms/${rid}`);
    notify.success("Dossier détruit.");
    load();
  };

  const showLog = async (lk) => {
    const { data } = await api.get(`/workspace/deal-links/links/${lk.id}/log`);
    setLog({ lk, accesses: data.accesses || [] });
  };

  return (
    <div className="space-y-4" data-testid="deal-links-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100 flex items-center gap-2"><Link2 className="w-5 h-5 text-[#43C6D8]" /> Liens de partage sécurisés</h1>
          <p className="text-zinc-500 text-xs mt-0.5">Dossiers à durée limitée, révocables à tout moment. Contrôle serveur à chaque ouverture, journal complet.</p>
        </div>
        <button onClick={() => setShowUpload(!showUpload)} className="bg-[#378ADD] hover:bg-[#2f7ac4] text-white rounded-md px-3 py-1.5 text-xs font-medium flex items-center gap-1.5" data-testid="dl-new-room-btn">
          <Plus className="w-3.5 h-3.5" /> Nouveau dossier (HTML / PDF)
        </button>
      </div>

      {showUpload && (
        <div className="noam-card p-4 flex flex-wrap items-center gap-2" data-testid="dl-upload-form">
          <input placeholder="Nom de code (ex. Dossier OL-14)" value={codeName} onChange={(e) => setCodeName(e.target.value)} className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-sm text-white flex-1 min-w-[200px]" data-testid="dl-code-name" />
          <input ref={fileRef} type="file" accept=".html,.htm,.pdf" className="text-xs text-zinc-400" data-testid="dl-file-input" />
          <button onClick={upload} disabled={busy} className="bg-white/10 hover:bg-white/15 text-white rounded px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="dl-upload-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Créer le dossier
          </button>
          <span className="text-[10px] text-zinc-600 w-full">Astuce : depuis le Studio de Présentation, un bouton « Deal Link » crée un dossier directement depuis une présentation générée (rattaché au deal).</span>
        </div>
      )}

      {loading ? <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div> : (
        rooms.length === 0 ? <div className="noam-card p-6 text-center text-zinc-500 text-sm">Aucun dossier. Créez-en un depuis le Studio ou par upload HTML/PDF.</div> : (
          <div className="space-y-3">
            {rooms.map((r) => (
              <div key={r.id} className="noam-card overflow-hidden" data-testid={`dl-room-${r.id}`}>
                <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
                  <FileText className="w-4 h-4 text-[#43C6D8]" />
                  <span className="text-sm font-medium text-zinc-100">{r.code_name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 text-zinc-500">{r.content_kind === "pdf" ? "PDF" : "HTML"}{r.destroyed_at ? " · détruit" : ""}</span>
                  {r.deal_id && <span className="text-[10px] text-zinc-600">rattaché au deal</span>}
                  <span className="ml-auto flex items-center gap-1.5">
                    {!r.destroyed_at && (
                      <>
                        <button onClick={() => setOpenRoom(openRoom === r.id ? null : r.id)} className="text-[11px] px-2 py-1 rounded border border-white/10 text-zinc-300 hover:text-white flex items-center gap-1" data-testid={`dl-add-link-${r.id}`}>
                          <Plus className="w-3 h-3" /> Lien destinataire <ChevronDown className={`w-3 h-3 transition-transform ${openRoom === r.id ? "rotate-180" : ""}`} />
                        </button>
                        <button onClick={() => closeAll(r.id)} className="text-[11px] px-2 py-1 rounded border border-white/10 text-zinc-500 hover:text-[#EF9F27]" data-testid={`dl-close-all-${r.id}`}>Tout fermer</button>
                        <button onClick={() => destroyRoom(r.id)} className="text-zinc-600 hover:text-[#E24B4A]" title="Détruire le dossier" data-testid={`dl-destroy-room-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </span>
                </div>
                {openRoom === r.id && <div className="px-4 pb-3"><NewLinkForm roomId={r.id} onCreated={load} /></div>}
                {(r.links || []).map((lk) => <LinkRow key={lk.id} lk={lk} onAction={action} onLog={showLog} />)}
              </div>
            ))}
          </div>
        )
      )}

      {log && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setLog(null)}>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 max-w-md w-full max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dl-log-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-white">Journal — {log.lk.recipient?.name}</div>
              <button onClick={() => setLog(null)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            {log.accesses.length === 0 ? <div className="text-xs text-zinc-500">Aucune ouverture pour l'instant.</div> : (
              <div className="space-y-1.5">
                {log.accesses.map((a) => (
                  <div key={a.id} className="text-[11px] text-zinc-400 flex items-center gap-2 border-t border-white/5 pt-1.5">
                    <span className="text-zinc-200">{a.at_zurich}</span>
                    <span className={a.event === "open" ? "text-[#1D9E75]" : a.event.startsWith("nda") ? "text-[#43C6D8]" : "text-[#E24B4A]"}>{a.event === "open" ? "ouverture" : a.event.startsWith("nda") ? `confidentialité acceptée (${a.event.split(":")[1] || ""})` : "accès refusé"}</span>
                    <span className="ml-auto text-zinc-600">{a.device}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
